import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface BusinessCaseRow {
  readonly businessCaseId: string;
  readonly businessCaseCode: string;
  readonly title: string;
  readonly isMajor: boolean;
  readonly recommendation: string | null;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED'];

/**
 * M33 — Business Case Management (§31 / §13). Register of business cases (T440)
 * backing major demands (BR-02), carrying the major-initiative flag, the
 * invest/defer/conditional/reject recommendation and the draft→submit→review→
 * approve lifecycle. Supports authoring a business case and driving it — submit,
 * review, approve and reject — each wired to the guarded /v1/pm/business-cases
 * write API. Filterable by status.
 */
export function BusinessCaseManagement(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<BusinessCaseRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newCost, setNewCost] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: BusinessCaseRow[] }>(`/v1/pm/business-cases${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.bc.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/business-cases`, {
        method: 'POST',
        body: {
          title: newTitle.trim(),
          ...(newCost ? { costEstimate: Number(newCost) } : {}),
          ...(newBenefit ? { benefitEstimate: Number(newBenefit) } : {}),
        },
        token,
      });
      setNotice(t('m33.bc.created', { name: newTitle.trim() }));
      setNewTitle('');
      setNewCost('');
      setNewBenefit('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'BUSINESS_CASE_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BusinessCaseRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/business-cases/${r.businessCaseId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.businessCaseCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="business-case-management">
      <h1>{t('m33.bc.title')}</h1>

      <section aria-label="create-business-case">
        <h2>{t('m33.bc.createHeading')}</h2>
        <form aria-label="business-case-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-business-case-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.bc.costEstimate')}<input aria-label="field-cost-estimate" type="number" value={newCost} onChange={(e) => setNewCost(e.target.value)} /></label>
          <label>{t('m33.bc.benefitEstimate')}<input aria-label="field-benefit-estimate" type="number" value={newBenefit} onChange={(e) => setNewBenefit(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-business-case" disabled={busy}>{busy ? t('m33.bc.creating') : t('m33.bc.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="business-case-error">{actionError}</p>}
        {notice && <p aria-label="business-case-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="business-case-count">{t('m33.bc.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="business-case-table"
            rows={rows}
            rowKey={(r) => r.businessCaseId}
            columns={[
              { key: 'businessCaseCode', header: t('m33.common.code'), render: (r) => r.businessCaseCode },
              { key: 'title', header: t('m33.common.title'), render: (r) => r.title },
              { key: 'isMajor', header: t('m33.bc.colMajor'), render: (r) => r.isMajor ? t('m33.common.yes') : t('m33.common.no') },
              { key: 'recommendation', header: t('m33.bc.colRecommendation'), render: (r) => r.recommendation ?? '—' },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.businessCaseCode}`} onClick={() => void act(r, 'submit', t('m33.bc.act.submitted'), 'BUSINESS_CASE_MANAGE')}>{t('m33.bc.btnSubmit')}</Button>
                  )}
                  {r.status === 'SUBMITTED' && (
                    <Button variant="secondary" type="button" aria-label={`review-${r.businessCaseCode}`} onClick={() => void act(r, 'review', t('m33.bc.act.underReview'), 'BUSINESS_CASE_APPROVE')}>{t('m33.bc.btnReview')}</Button>
                  )}
                  {r.status === 'UNDER_REVIEW' && (
                    <>
                      <input aria-label={`recommendation-${r.businessCaseCode}`} value={inputs[r.businessCaseId] ?? ''} onChange={(e) => setInputs((m) => ({ ...m, [r.businessCaseId]: e.target.value }))} placeholder={t('m33.bc.recommendationPh')} />
                      <Button variant="secondary" type="button" aria-label={`approve-${r.businessCaseCode}`} onClick={() => void act(r, 'approve', t('m33.bc.act.approved'), 'BUSINESS_CASE_APPROVE', { recommendation: inputs[r.businessCaseId] || undefined })}>{t('m33.bc.btnApprove')}</Button>
                      <input aria-label={`reject-reason-${r.businessCaseCode}`} value={inputs[`rej-${r.businessCaseId}`] ?? ''} onChange={(e) => setInputs((m) => ({ ...m, [`rej-${r.businessCaseId}`]: e.target.value }))} placeholder={t('m33.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.businessCaseCode}`} onClick={() => void act(r, 'reject', t('m33.bc.act.rejected'), 'BUSINESS_CASE_APPROVE', { reason: inputs[`rej-${r.businessCaseId}`] ?? '' })}>{t('m33.bc.btnReject')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
